import { browser } from '$app/environment';
import type { Cookies } from '@sveltejs/kit';
import { get, writable } from 'svelte/store';
import { ETheme } from '../dto/ETheme';
import { EThemeKey } from './EThemeKey';
import { isInWebWorker } from './navigationUtils';

export const DEFAULT_THEME = determineDefaultTheme();

export const appTheme = writable<ETheme>();

export function getThemeFromCookies(cookies: Cookies): ETheme {
	const theme = cookies.get('theme');
	if (theme && ['light', 'dark'].includes(theme)) {
		return theme as ETheme;
	}
	return ETheme.Dark;
}

export function getAppThemeOrDefault(): ETheme {
	return (get(appTheme) as ETheme) || DEFAULT_THEME;
}

export function getThemeColor(key: EThemeKey): string {
	if (getAppThemeOrDefault() == ETheme.Dark) {
		return DarkThemeMapping[key];
	} else {
		return LightThemeMapping[key];
	}
}

export function switchTheme() {
	let newTheme = ETheme.Dark;
	if (getAppThemeOrDefault() === ETheme.Dark) {
		newTheme = ETheme.Light;
	}
	appTheme.set(newTheme);
	if (newTheme == ETheme.Dark) {
		document.documentElement.classList.add('dark');
	} else {
		document.documentElement.classList.remove('dark');
	}
	document.cookie = `theme=${newTheme};max-age=31536000;path="/"`;
}

const DarkThemeMapping: Record<EThemeKey, string> = {
	[EThemeKey.MinimapAddedLineColor]: '#68d391',
	[EThemeKey.MinimapChangedLineColor]: '#1888eb',
	[EThemeKey.MinimapRemovedLineColor]: '#dc2626',
	[EThemeKey.AddedFileForeground]: '#5dd288',
	[EThemeKey.MovedFileForeground]: '#9989eb',
	[EThemeKey.MinorEditForeground]: '#h049dcc',
	[EThemeKey.DeletedFileForeground]: '#dd6f7a',
	[EThemeKey.MinimapCommentColor]: '#a8b1ff'
};

const LightThemeMapping: Record<EThemeKey, string> = {
	[EThemeKey.MinimapAddedLineColor]: '#e6ffed',
	[EThemeKey.MinimapChangedLineColor]: '#ffeef0',
	[EThemeKey.MinimapRemovedLineColor]: '#b91c1c',
	[EThemeKey.AddedFileForeground]: '#2e8651',
	[EThemeKey.MinorEditForeground]: '#4aa1ea',
	[EThemeKey.MovedFileForeground]: '#6530b8',
	[EThemeKey.DeletedFileForeground]: '#b12426',
	[EThemeKey.MinimapCommentColor]: '#D58BFF'
};

function determineDefaultTheme(): ETheme {
	// Check if the user has a global theme preference set
	if (
		!browser ||
		isInWebWorker() ||
		(browser && window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches)
	) {
		return ETheme.Dark;
	}
	return ETheme.Light;
}
